import React, { useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, Loader2, Link2, Lock, Play, Users } from 'lucide-react';
import { api, type QuizSummary } from '../lib/api';
import { ShareButton } from './ShareButton';

interface Props {
  quizId: string;
  onHost: (quiz: QuizSummary) => void;
  onStudy: (quizId: string) => void;
  onOpenUser: (userId: string) => void;
  onBack: () => void;
}

/**
 * Ficha de una trivia a la que se llega por su enlace (`/t/<id>`).
 *
 * Quien recibe el enlace no sabe qué es Open Dahu ni tiene por qué tener
 * cuenta: la ficha le dice qué trivia es y le deja jugarla a su ritmo sin más,
 * o abrir una sala para jugarla con otros.
 */
export const SharedQuizView: React.FC<Props> = ({ quizId, onHost, onStudy, onOpenUser, onBack }) => {
  const [quiz, setQuiz] = useState<QuizSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setQuiz(null);
    setError('');
    void (async () => {
      try {
        const data = await api.get<{ quiz: QuizSummary }>(`/quizzes/${encodeURIComponent(quizId)}`);
        setQuiz(data.quiz);
        document.title = `${data.quiz.title} · Open Dahu`;
      } catch {
        setError('Esta trivia no existe, es privada o su autor la borró.');
      }
    })();
  }, [quizId]);

  // Al salir de la ficha, la pestaña vuelve a llamarse como el sitio.
  useEffect(() => {
    const original = document.title;
    return () => {
      document.title = original;
    };
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10 space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-100 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Ver todas las trivias
      </button>

      {error ? (
        <div className="surface p-10 text-center space-y-3">
          <p className="text-ink-300">{error}</p>
          <button onClick={onBack} className="text-sky-400 hover:text-sky-300 font-medium">
            Ir al catálogo
          </button>
        </div>
      ) : !quiz ? (
        <div className="surface p-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
        </div>
      ) : (
        <article className="surface p-5 sm:p-8 space-y-5">
          <div className="flex items-start gap-4">
            <span className="font-emoji text-5xl sm:text-6xl leading-none shrink-0" aria-hidden="true">
              {quiz.coverEmoji}
            </span>
            <div className="min-w-0 flex-1 space-y-1.5">
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-ink-50 leading-tight break-words">
                {quiz.title}
              </h1>
              <p className="text-ink-500 text-sm flex items-center gap-1.5 flex-wrap">
                <span>{quiz.category}</span>
                <span aria-hidden="true">·</span>
                <span>
                  {quiz.questionCount} {quiz.questionCount === 1 ? 'pregunta' : 'preguntas'}
                </span>
                {quiz.languageName && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{quiz.languageName}</span>
                  </>
                )}
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {quiz.playsCount}
                </span>
                {quiz.visibility !== 'public' && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="inline-flex items-center gap-1 text-ink-400">
                          {quiz.visibility === 'private' ? (
                            <Lock className="w-3.5 h-3.5" />
                          ) : (
                            <Link2 className="w-3.5 h-3.5" />
                          )}
                          {quiz.visibility === 'private' ? 'Privada' : 'No listada'}
                        </span>
                      </>
                    )}
              </p>
              <p className="text-ink-400 text-sm">
                Por{' '}
                {quiz.creatorId ? (
                  <button
                    onClick={() => onOpenUser(quiz.creatorId!)}
                    className="font-medium text-ink-200 hover:text-sky-300 transition-colors"
                  >
                    {quiz.creatorName}
                  </button>
                ) : (
                  <span className="font-medium text-ink-200">{quiz.creatorName}</span>
                )}
              </p>
            </div>
          </div>

          {quiz.description && <p className="text-ink-300 whitespace-pre-line">{quiz.description}</p>}

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={() => onStudy(quiz.id)}
              className="sm:flex-1 flex items-center justify-center gap-2 h-12 rounded-lg font-semibold
                         bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              Jugar ahora
            </button>
            <button
              onClick={() => onHost(quiz)}
              className="sm:flex-1 flex items-center justify-center gap-2 h-12 rounded-lg font-semibold
                         bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors"
              title="Abrir una sala con PIN para jugarla con otros"
            >
              <Play className="w-4 h-4" fill="currentColor" />
              Jugar en grupo
            </button>
            <ShareButton
              quiz={quiz}
              withLabel
              className="w-full flex items-center justify-center gap-2 h-12 px-4 rounded-lg font-semibold
                         bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-200 transition-colors"
            />
          </div>
        </article>
      )}
    </div>
  );
};
